import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, InputGroup, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { 
  FaArrowLeft, FaSave, FaUser, FaEnvelope, FaPhone, FaLock, FaIdCard, 
  FaFileAlt, FaMapMarkerAlt, FaCalendarAlt, FaVenusMars, FaHome, 
  FaCreditCard, FaPencilAlt, FaBuilding, FaMapPin, FaFileUpload
} from 'react-icons/fa';
import { dsoApi } from '../../services/api';
import { locationApi } from '../../services/location';
import Select from 'react-select';
import Swal from 'sweetalert2';
import './AddAgent.css';

function AddAgent() {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('profile');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [validated, setValidated] = useState(false);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [pincodes, setPincodes] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingPincodes, setLoadingPincodes] = useState(false);

  // Form Data
  const [formData, setFormData] = useState({
    // Basic profile info (User table)
    name: '',
    email: '',
    mobile: '',
    password: '',
    gender: 'MALE',

    // Personal details (AgentProfile)
    date_of_birth: '',
    address: '',
    state_id: '',
    city_id: '',
    pincode: '',

    // Document details
    pan_card_no: '',
    adhaar_card_no: '',
    
    // File uploads will be handled separately
    pan_card_file: null,
    adhaar_card_front_file: null,
    adhaar_card_back_file: null
  });

  // Navigation items for sections
  const navItems = [
    { id: 'profile', label: 'Basic Information', icon: <FaUser size={16} /> },
    { id: 'personal', label: 'Personal Details', icon: <FaIdCard size={16} /> },
    { id: 'documents', label: 'Documents', icon: <FaFileAlt size={16} /> }
  ];

  // Load states on component mount
  useEffect(() => {
    const fetchStates = async () => {
      setLoadingStates(true);
      try {
        const response = await locationApi.getStates();
        // Make sure we handle the correct response structure
        if (response.data && response.data.states) {
          setStates(response.data.states);
        } else {
          console.error("Unexpected states data structure:", response.data);
          setStates([]);
        }
      } catch (err) {
        console.error("Error fetching states:", err);
        setStates([]);
      } finally {
        setLoadingStates(false);
      }
    };

    fetchStates();
  }, []);

  // Load cities when state changes
  useEffect(() => {
    if (formData.state_id) {
      const fetchCities = async () => {
        setLoadingCities(true);
        setCities([]); // Clear cities when loading new ones
        try {
          const response = await locationApi.getCities(formData.state_id);
          // Make sure we handle the correct response structure
          if (response.data && response.data.cities) {
            setCities(response.data.cities);
          } else {
            console.error("Unexpected cities data structure:", response.data);
            setCities([]);
          }
        } catch (err) {
          console.error("Error fetching cities:", err);
          setCities([]);
        } finally {
          setLoadingCities(false);
        }
      };

      fetchCities();
    } else {
      setCities([]);
    }
  }, [formData.state_id]);
  
  // Load pincodes when city changes
  useEffect(() => {
    if (formData.city_id) {
      const fetchPincodes = async () => {
        setLoadingPincodes(true);
        setPincodes([]); // Clear pincodes when loading new ones
        try {
          const response = await locationApi.getPincodes(formData.city_id);
          // Make sure we handle the correct response structure
          if (response.data && response.data.pincodes) {
            setPincodes(response.data.pincodes);
          } else {
            console.error("Unexpected pincodes data structure:", response.data);
            setPincodes([]);
          }
        } catch (err) {
          console.error("Error fetching pincodes:", err);
          setPincodes([]);
        } finally {
          setLoadingPincodes(false);
        }
      };

      fetchPincodes();
    } else {
      setPincodes([]);
    }
  }, [formData.city_id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Validation for specific fields
    if (name === 'mobile' && value !== '' && !/^[0-9]*$/.test(value)) {
      // Don't update if the value is not numeric
      return;
    }
    
    // Specific validation for PAN - convert to uppercase
    if (name === 'pan_card_no') {
      setFormData({
        ...formData,
        [name]: value.toUpperCase()
      });
    } else {
      // Normal case for other fields
      setFormData({
        ...formData,
        [name]: value
      });
    }
    
    // Clear specific error if user starts typing
    if (error && error.toLowerCase().includes(name)) {
      setError('');
    }
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    try {
      if (files && files[0]) {
        // Check if file size is acceptable (max 2MB)
        if (files[0].size > 2 * 1024 * 1024) {
          setError(`File ${files[0].name} is too large. Maximum size is 2MB.`);
          // Reset the file input
          e.target.value = '';
          return;
        }
        
        // Check file type
        const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
        if (!allowedTypes.includes(files[0].type)) {
          setError(`Only JPEG, PNG, and PDF files are allowed.`);
          // Reset the file input
          e.target.value = '';
          return;
        }
        
        // Clear any previous errors when a valid file is selected
        setError('');
        
        // Update form data with the file
        setFormData({
          ...formData,
          [name]: files[0]
        });
      }
    } catch (err) {
      console.error('Error handling file:', err);
      setError('There was an error processing the selected file. Please try again.');
      // Reset the file input
      e.target.value = '';
    }
  };

  const validateForm = () => {
    // Validate based on active section
    if (activeSection === 'profile') {
      if (!formData.name.trim()) return "Name is required";
      if (!formData.email.trim()) return "Email is required";
      if (!/\S+@\S+\.\S+/.test(formData.email)) return "Email format is invalid";
      if (!formData.mobile.trim()) return "Mobile number is required";
      if (!/^[6-9]\d{9}$/.test(formData.mobile)) return "Mobile number must be 10 digits starting with 6-9";
      if (!formData.password || formData.password.length < 8) return "Password must be at least 8 characters";
    } 
    else if (activeSection === 'personal') {
      // Date of birth and address are optional but could have format validation
      if (formData.date_of_birth && !/^\d{4}-\d{2}-\d{2}$/.test(formData.date_of_birth)) {
        return "Date of birth format should be YYYY-MM-DD";
      }
    }
    else if (activeSection === 'documents') {
      // PAN and Aadhaar validation
      if (formData.pan_card_no && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.pan_card_no)) {
        return "PAN card number format is invalid";
      }
      if (formData.adhaar_card_no && !/^\d{12}$/.test(formData.adhaar_card_no)) {
        return "Aadhaar card number must be 12 digits";
      }
    }
    return null;
  };

  const handleSectionSave = (e) => {
    e.preventDefault();
    
    // Validate the current section
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    // Move to next section or submit form
    if (activeSection === 'profile') {
      setActiveSection('personal');
    } 
    else if (activeSection === 'personal') {
      setActiveSection('documents');
    }
    else if (activeSection === 'documents') {
      handleSubmit();
    }

    // Clear errors and set success message
    setError('');
    setSuccess('Section saved successfully!');

    // Hide success message after 3 seconds
    setTimeout(() => {
      setSuccess('');
    }, 3000);
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    setError('');

    try {
      // First, create the agent with basic details
      const basicData = {
        name: formData.name,
        email: formData.email,
        mobile: formData.mobile,
        password: formData.password,
        gender: formData.gender
      };

      const response = await dsoApi.createAgent(basicData);
      const agentId = response.data.id;

      // Create FormData for document uploads
      if (formData.pan_card_file || formData.adhaar_card_front_file || formData.adhaar_card_back_file) {
        const documentsFormData = new FormData();
        
        // Add agent and document details
        documentsFormData.append('agent_id', agentId);
        
        if (formData.pan_card_no) {
          documentsFormData.append('pan_card_no', formData.pan_card_no);
        }
        
        if (formData.adhaar_card_no) {
          documentsFormData.append('adhaar_card_no', formData.adhaar_card_no);
        }
        
        // Add file uploads
        if (formData.pan_card_file) {
          documentsFormData.append('pan_card_file', formData.pan_card_file);
        }
        
        if (formData.adhaar_card_front_file) {
          documentsFormData.append('adhaar_card_front_file', formData.adhaar_card_front_file);
        }
        
        if (formData.adhaar_card_back_file) {
          documentsFormData.append('adhaar_card_back_file', formData.adhaar_card_back_file);
        }

        // Update agent with documents - with improved error handling
        try {
          await dsoApi.uploadAgentDocument(agentId, documentsFormData);
        } catch (uploadError) {
          console.error('Error uploading documents:', uploadError);
          // Continue with the agent creation process even if document upload fails
          // Just log the error but don't throw it to prevent interrupting the whole flow
        }
      }

      // Update agent with additional information
      const updateData = {
        date_of_birth: formData.date_of_birth || undefined,
        address: formData.address || undefined,
        state_id: formData.state_id || undefined,
        city_id: formData.city_id || undefined,
        pincode: formData.pincode || undefined
      };

      // Only include fields that have values
      const filteredUpdateData = Object.entries(updateData)
        .filter(([_, value]) => value !== undefined && value !== '')
        .reduce((obj, [key, value]) => ({ ...obj, [key]: value }), {});

      // Update agent if there are additional fields
      if (Object.keys(filteredUpdateData).length > 0) {
        await dsoApi.updateAgent(agentId, filteredUpdateData);
      }

      // Show success message
      Swal.fire({
        icon: 'success',
        title: 'Agent Added!',
        text: `${formData.name} has been successfully added to your team.`,
        timer: 2500,
        showConfirmButton: false
      });

      // Navigate back to team list
      navigate('/dso/team');

    } catch (err) {
      console.error('Error creating agent:', err);
      // Handle error response properly, ensuring we don't try to render an object
      let errorMsg = 'Failed to add agent. Please check the details and try again.';
      
      if (err.response?.data?.detail) {
        // Handle possible validation error object
        const errorDetail = err.response.data.detail;
        if (typeof errorDetail === 'string') {
          errorMsg = errorDetail;
        } else if (Array.isArray(errorDetail)) {
          // Handle FastAPI validation errors array
          errorMsg = errorDetail.map(err => err.msg).join(', ');
        } else if (typeof errorDetail === 'object') {
          // Handle object with validation errors
          errorMsg = Object.values(errorDetail).map(val => 
            typeof val === 'string' ? val : JSON.stringify(val)
          ).join(', ');
        }
      }
      
      setError(errorMsg);
      
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMsg,
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Render content based on active section
  const renderContent = () => {
    switch (activeSection) {
      case 'profile':
        return (
          <>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>
                <h5 className="mb-0">Basic Information</h5>
              </div>
              <Badge bg="primary">Step 1/3</Badge>
            </Card.Header>
            <Card.Body>
              {error && <Alert variant="danger">{typeof error === 'string' ? error : 'An error occurred. Please try again.'}</Alert>}
              {success && <Alert variant="success">{typeof success === 'string' ? success : 'Operation completed successfully.'}</Alert>}
              
              <Form noValidate validated={validated} onSubmit={handleSectionSave} className="agent-form">
                <Row>
                  {/* Name */}
                  <Col md={6}>
                    <Form.Group className="mb-4" controlId="formAgentName">
                      <Form.Label>
                        Full Name <span className="text-danger">*</span>
                      </Form.Label>
                      <InputGroup hasValidation>
                        <InputGroup.Text><FaUser /></InputGroup.Text>
                        <Form.Control
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          placeholder="Enter agent's full name"
                          className="form-control-lg"
                        />
                        <Form.Control.Feedback type="invalid">
                          Please provide the agent's name.
                        </Form.Control.Feedback>
                      </InputGroup>
                    </Form.Group>
                  </Col>

                  {/* Email */}
                  <Col md={6}>
                    <Form.Group className="mb-4" controlId="formAgentEmail">
                      <Form.Label>
                        Email Address <span className="text-danger">*</span>
                      </Form.Label>
                      <InputGroup hasValidation>
                        <InputGroup.Text><FaEnvelope /></InputGroup.Text>
                        <Form.Control
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          placeholder="Enter agent's email"
                          className="form-control-lg"
                        />
                        <Form.Control.Feedback type="invalid">
                          Please provide a valid email address.
                        </Form.Control.Feedback>
                      </InputGroup>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  {/* Mobile */}
                  <Col md={6}>
                    <Form.Group className="mb-4" controlId="formAgentMobile">
                      <Form.Label>
                        Mobile Number <span className="text-danger">*</span>
                      </Form.Label>
                      <InputGroup hasValidation>
                        <InputGroup.Text><FaPhone /></InputGroup.Text>
                        <Form.Control
                          type="tel"
                          name="mobile"
                          value={formData.mobile}
                          onChange={handleInputChange}
                          required
                          pattern="^[6-9][0-9]{9}$" // Basic Indian mobile pattern
                          placeholder="Enter 10-digit mobile number"
                          className="form-control-lg"
                          maxLength={10}
                        />
                        <Form.Control.Feedback type="invalid">
                          Please provide a valid 10-digit mobile number starting with 6, 7, 8, or 9.
                        </Form.Control.Feedback>
                      </InputGroup>
                    </Form.Group>
                  </Col>

                  {/* Password */}
                  <Col md={6}>
                    <Form.Group className="mb-4" controlId="formAgentPassword">
                      <Form.Label>
                        Password <span className="text-danger">*</span>
                      </Form.Label>
                      <InputGroup hasValidation>
                        <InputGroup.Text><FaLock /></InputGroup.Text>
                        <Form.Control
                          type="password"
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          required
                          minLength={8}
                          placeholder="Enter initial password (min 8 characters)"
                          className="form-control-lg"
                        />
                        <Form.Control.Feedback type="invalid">
                          Password must be at least 8 characters long.
                        </Form.Control.Feedback>
                      </InputGroup>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  {/* Gender */}
                  <Col md={6}>
                    <Form.Group className="mb-4" controlId="formAgentGender">
                      <Form.Label>
                        Gender
                      </Form.Label>
                      <InputGroup>
                        <InputGroup.Text><FaVenusMars /></InputGroup.Text>
                        <Form.Select 
                          name="gender" 
                          value={formData.gender}
                          onChange={handleInputChange}
                          className="form-select-lg"
                        >
                          <option value="MALE">Male</option>
                          <option value="FEMALE">Female</option>
                        </Form.Select>
                      </InputGroup>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Action Buttons */}
                <div className="d-flex justify-content-end mt-4">
                  <Button
                    variant="outline-secondary"
                    className="agent-btn agent-btn-outline me-2"
                    onClick={() => navigate('/dso/team')}
                    disabled={isSaving}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    className="agent-btn agent-btn-primary"
                    type="submit"
                    disabled={isSaving}
                  >
                    Next
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </>
        );

      case 'personal':
        return (
          <>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>
                <h5 className="mb-0">Personal Details</h5>
              </div>
              <Badge bg="primary">Step 2/3</Badge>
            </Card.Header>
            <Card.Body>
              {error && <Alert variant="danger">{typeof error === 'string' ? error : 'An error occurred. Please try again.'}</Alert>}
              {success && <Alert variant="success">{typeof success === 'string' ? success : 'Operation completed successfully.'}</Alert>}
              
              <Form noValidate validated={validated} onSubmit={handleSectionSave} className="agent-form">
                <Row>
                  {/* Date of Birth */}
                  <Col md={6}>
                    <Form.Group className="mb-4" controlId="formAgentDOB">
                      <Form.Label>
                        Date of Birth
                      </Form.Label>
                      <InputGroup>
                        <InputGroup.Text><FaCalendarAlt /></InputGroup.Text>
                        <Form.Control
                          type="date"
                          name="date_of_birth"
                          value={formData.date_of_birth}
                          onChange={handleInputChange}
                          placeholder="YYYY-MM-DD"
                          className="form-control-lg"
                        />
                      </InputGroup>
                    </Form.Group>
                  </Col>

                  {/* Address */}
                  <Col md={6}>
                    <Form.Group className="mb-4" controlId="formAgentAddress">
                      <Form.Label>
                        Address
                      </Form.Label>
                      <InputGroup>
                        <InputGroup.Text><FaMapMarkerAlt /></InputGroup.Text>
                        <Form.Control
                          type="text"
                          name="address"
                          value={formData.address}
                          onChange={handleInputChange}
                          placeholder="Enter agent's address"
                          className="form-control-lg"
                        />
                      </InputGroup>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  {/* State */}
                  <Col md={4}>
                    <Form.Group className="mb-3" controlId="formAgentState">
                      <Form.Label>
                        State
                      </Form.Label>
                      <InputGroup>
                        <div className="flex-grow-1">
                          <Select
                            name="state_id"
                            options={states.map(state => ({
                              value: state.id,
                              label: state.name
                            }))}
                            value={formData.state_id ? {
                              value: formData.state_id,
                              label: states.find(s => s.id === parseInt(formData.state_id))?.name || ''
                            } : null}
                            onChange={(selected) => {
                              setFormData({
                                ...formData,
                                state_id: selected ? selected.value : '',
                                city_id: '', // Reset city when state changes
                                pincode_id: '', // Also reset pincode
                                pincode: '',
                              });
                            }}
                            placeholder="Select State"
                            isClearable
                            className="react-select-container"
                            classNamePrefix="react-select"
                            isLoading={loadingStates}
                            menuPortalTarget={document.body}
                            menuPosition={'fixed'}
                            styles={{
                              control: (base) => ({
                                ...base,
                                borderTopLeftRadius: 0,
                                borderBottomLeftRadius: 0,
                                minHeight: "31px",
                                height: "31px"
                              }),
                              valueContainer: (base) => ({
                                ...base,
                                height: "31px",
                                padding: "0 8px",
                              }),
                              input: (base) => ({
                                ...base,
                                margin: 0,
                                padding: 0,
                              }),
                              // Fix z-index issues with dropdown
                              menu: (base) => ({
                                ...base,
                                zIndex: 9999
                              }),
                              menuPortal: (base) => ({
                                ...base, 
                                zIndex: 9999
                              })
                            }}
                          />
                        </div>
                      </InputGroup>
                    </Form.Group>
                  </Col>

                  {/* City */}
                  <Col md={4}>
                    <Form.Group className="mb-3" controlId="formAgentCity">
                      <Form.Label>
                        City
                      </Form.Label>
                      <InputGroup>
                        <div className="flex-grow-1">
                          <Select
                            name="city_id"
                            options={cities.map(city => ({
                              value: city.id,
                              label: city.name
                            }))}
                            value={formData.city_id ? {
                              value: formData.city_id,
                              label: cities.find(c => c.id === parseInt(formData.city_id))?.name || ''
                            } : null}
                            onChange={(selected) => {
                              setFormData({
                                ...formData,
                                city_id: selected ? selected.value : '',
                                pincode_id: '', // Reset pincode when city changes
                                pincode: '',
                              });
                            }}
                            placeholder="Select City"
                            isClearable
                            isDisabled={!formData.state_id || loadingCities}
                            isLoading={loadingCities}
                            menuPortalTarget={document.body}
                            menuPosition={'fixed'}
                            className="react-select-container"
                            classNamePrefix="react-select"
                            styles={{
                              control: (base) => ({
                                ...base,
                                borderTopLeftRadius: 0,
                                borderBottomLeftRadius: 0,
                                minHeight: "38px",
                                height: "38px"
                              }),
                              valueContainer: (base) => ({
                                ...base,
                                height: "38px",
                                padding: "0 8px",
                              }),
                              input: (base) => ({
                                ...base,
                                margin: 0,
                                padding: 0,
                              }),
                              // Fix z-index issues with dropdown
                              menu: (base) => ({
                                ...base,
                                zIndex: 9998
                              })
                            }}
                          />
                        </div>
                      </InputGroup>
                    </Form.Group>
                  </Col>

                  {/* Pincode */}
                  <Col md={4}>
                    <Form.Group className="mb-3" controlId="formAgentPincode">
                      <Form.Label>
                        Pincode
                      </Form.Label>
                      <InputGroup>
                        <div className="flex-grow-1">
                          <Select
                            name="pincode_id"
                            options={pincodes.map(pincode => ({
                              value: pincode.id,
                              label: pincode.code
                            }))}
                            value={formData.pincode_id ? {
                              value: formData.pincode_id,
                              label: pincodes.find(p => p.id === parseInt(formData.pincode_id))?.code || formData.pincode
                            } : null}
                            onChange={(selected) => {
                              setFormData({
                                ...formData,
                                pincode_id: selected ? selected.value : '',
                                pincode: selected ? selected.label : '' // Also store the pincode code
                              });
                            }}
                            placeholder="Select Pincode"
                            isClearable
                            isDisabled={!formData.city_id || loadingPincodes}
                            isLoading={loadingPincodes}
                            menuPortalTarget={document.body}
                            menuPosition={'fixed'}
                            className="react-select-container"
                            classNamePrefix="react-select"
                            styles={{
                              control: (base) => ({
                                ...base,
                                borderTopLeftRadius: 0,
                                borderBottomLeftRadius: 0,
                                minHeight: "38px",
                                height: "38px"
                              }),
                              valueContainer: (base) => ({
                                ...base,
                                height: "38px",
                                padding: "0 8px",
                              }),
                              input: (base) => ({
                                ...base,
                                margin: 0,
                                padding: 0,
                              }),
                              // Fix z-index issues with dropdowns
                              menu: (base) => ({
                                ...base,
                                zIndex: 9999
                              })
                            }}
                          />
                        </div>
                      </InputGroup>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Action Buttons */}
                <div className="d-flex justify-content-end mt-4">
                  <Button
                    variant="outline-secondary"
                    className="agent-btn agent-btn-outline me-2"
                    onClick={() => setActiveSection('profile')}
                    disabled={isSaving}
                  >
                    Back
                  </Button>
                  <Button
                    variant="primary"
                    className="agent-btn agent-btn-primary"
                    type="submit"
                    disabled={isSaving}
                  >
                    Next
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </>
        );

      case 'documents':
        return (
          <>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>
                <h5 className="mb-0">Documents</h5>
              </div>
              <Badge bg="primary">Step 3/3</Badge>
            </Card.Header>
            <Card.Body>
              {error && <Alert variant="danger">{typeof error === 'string' ? error : 'An error occurred. Please try again.'}</Alert>}
              {success && <Alert variant="success">{typeof success === 'string' ? success : 'Operation completed successfully.'}</Alert>}
              
              <Form noValidate validated={validated} onSubmit={handleSectionSave} className="agent-form">
                <Row>
                  {/* PAN Card Number */}
                  <Col md={6}>
                    <Form.Group className="mb-4" controlId="formAgentPAN">
                      <Form.Label>
                        PAN Card Number
                      </Form.Label>
                      <InputGroup>
                        <InputGroup.Text><FaCreditCard /></InputGroup.Text>
                        <Form.Control
                          type="text"
                          name="pan_card_no"
                          value={formData.pan_card_no}
                          onChange={handleInputChange}
                          placeholder="e.g. ABCDE1234F"
                          pattern="[A-Z]{5}[0-9]{4}[A-Z]{1}"
                          className="form-control-lg"
                        />
                        <Form.Control.Feedback type="invalid">
                          Please enter a valid PAN card number (format: ABCDE1234F).
                        </Form.Control.Feedback>
                      </InputGroup>
                    </Form.Group>
                  </Col>

                  {/* PAN Card Upload */}
                  <Col md={6}>
                    <Form.Group className="mb-4" controlId="formAgentPANFile">
                      <Form.Label>
                        PAN Card Upload
                      </Form.Label>
                      <InputGroup>
                        <InputGroup.Text><FaFileUpload /></InputGroup.Text>
                        <Form.Control
                          type="file"
                          name="pan_card_file"
                          onChange={handleFileChange}
                          accept="image/jpeg,image/png,application/pdf"
                          className="form-control-lg"
                        />
                      </InputGroup>
                      <Form.Text className="text-muted">
                        Upload PAN card image/PDF (Max: 2MB)
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  {/* Aadhaar Card Number */}
                  <Col md={6}>
                    <Form.Group className="mb-4" controlId="formAgentAadhaar">
                      <Form.Label>
                        Aadhaar Card Number
                      </Form.Label>
                      <InputGroup>
                        <InputGroup.Text><FaIdCard /></InputGroup.Text>
                        <Form.Control
                          type="text"
                          name="adhaar_card_no"
                          value={formData.adhaar_card_no}
                          onChange={handleInputChange}
                          placeholder="e.g. 123456789012"
                          pattern="[0-9]{12}"
                          maxLength={12}
                          className="form-control-lg"
                        />
                        <Form.Control.Feedback type="invalid">
                          Please enter a valid 12-digit Aadhaar card number.
                        </Form.Control.Feedback>
                      </InputGroup>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  {/* Aadhaar Front Upload */}
                  <Col md={6}>
                    <Form.Group className="mb-4" controlId="formAgentAadhaarFront">
                      <Form.Label>
                        Aadhaar Card Front
                      </Form.Label>
                      <InputGroup>
                        <InputGroup.Text><FaFileUpload /></InputGroup.Text>
                        <Form.Control
                          type="file"
                          name="adhaar_card_front_file"
                          onChange={handleFileChange}
                          accept="image/jpeg,image/png,application/pdf"
                          className="form-control-lg"
                        />
                      </InputGroup>
                      <Form.Text className="text-muted">
                        Upload Aadhaar front image/PDF (Max: 2MB)
                      </Form.Text>
                    </Form.Group>
                  </Col>

                  {/* Aadhaar Back Upload */}
                  <Col md={6}>
                    <Form.Group className="mb-4" controlId="formAgentAadhaarBack">
                      <Form.Label>
                        Aadhaar Card Back
                      </Form.Label>
                      <InputGroup>
                        <InputGroup.Text><FaFileUpload /></InputGroup.Text>
                        <Form.Control
                          type="file"
                          name="adhaar_card_back_file"
                          onChange={handleFileChange}
                          accept="image/jpeg,image/png,application/pdf"
                          className="form-control-lg"
                        />
                      </InputGroup>
                      <Form.Text className="text-muted">
                        Upload Aadhaar back image/PDF (Max: 2MB)
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Action Buttons */}
                <div className="d-flex justify-content-end mt-4">
                  <Button
                    variant="outline-secondary"
                    className="agent-btn agent-btn-outline me-2"
                    onClick={() => setActiveSection('personal')}
                    disabled={isSaving}
                  >
                    Back
                  </Button>
                  <Button
                    variant="primary"
                    className="agent-btn agent-btn-primary"
                    type="submit"
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>
                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                        <span className="ms-1">Saving Agent...</span>
                      </>
                    ) : (
                      <>
                        <FaSave className="me-1" /> Add Agent
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </>
        );

      default:
        return <p>Select a section from the menu</p>;
    }
  };

  return (
    <Container fluid className="add-agent-container">
      {/* Header */}
      <div className="add-agent-header">
        <FaArrowLeft
          className="back-icon me-2"
          onClick={() => navigate('/dso/team')}
          title="Back to Team"
        />
        <div>
          <h2 className="add-agent-title">Add New Agent</h2>
          <p className="add-agent-subtitle">Enter the details for the new agent.</p>
        </div>
      </div>

      <div className="agent-settings-content">
        {/* Left section - Navigation */}
        <div className="agent-settings-left">
          <ul className="agent-settings-nav">
            {navItems.map(item => (
              <li key={item.id} className="agent-settings-nav-item">
                <div 
                  className={`agent-settings-nav-link ${activeSection === item.id ? 'active' : ''}`}
                  onClick={() => setActiveSection(item.id)}
                >
                  <span className="agent-settings-nav-icon">{item.icon}</span>
                  <span>{item.label}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Right section - Content */}
        <div className="agent-settings-right">
          <Card className="add-agent-card">
            {renderContent()}
          </Card>
        </div>
      </div>
    </Container>
  );
}

export default AddAgent;